import { useQuery } from '@tanstack/react-query'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import { useAxiosInstance } from '../useAxiosInstance'
import { useStreamedQuery } from '@/hooks/useStreamedQuery'
import { v4 as uuidv4 } from 'uuid'
import { useMemo, useCallback, useEffect, useRef } from 'react'
import {
    GetActiveManagementRequestParams,
    GetIntervalDataResponseBody,
    IntervalDataEntry,
    GetTimezonesRequestParams,
    GetTimezonesResponseBody,
    StreamedIntervalDataResponse,
    IntervalHighlightsState,
    IntervalReportSettings,
    DataTypeParam,
    IntervalDisplayParam,
    IntervalDataExportErrorBody,
} from './types'
import { logError } from '@/utils/logError'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

const INTERVAL_HIGHLIGHTS_COMPARE_CAP = 20
const INTERVAL_HIGHLIGHTS_AGGREGATE_TOP = 5

/** Keys allowed for the interval-data API. Front-end-only fields (e.g. showHighlights, granularity) must not be sent. */
const INTERVAL_DATA_API_KEYS: ReadonlyArray<
    keyof GetActiveManagementRequestParams
> = [
    'nodeIds[]',
    'tagIds[]',
    'facilityIds[]',
    'usnAccountIds[]',
    'meterIds[]',
    'subMeterIds[]',
    'startDate',
    'endDate',
    'type',
    'timeInterval',
    'timezone',
    'dataGrouping',
    'dataType',
    'sortOrder',
]

function getIntervalDataApiParams(
    params: Partial<GetActiveManagementRequestParams>
): Partial<GetActiveManagementRequestParams> {
    const result: Partial<GetActiveManagementRequestParams> = {}
    for (const key of INTERVAL_DATA_API_KEYS) {
        const value = params[key]
        if (value !== undefined && value !== '') {
            ;(result as Record<string, unknown>)[key] = value
        }
    }
    return result
}

function getEntryValue(
    entry: IntervalDataEntry,
    dataType: DataTypeParam
): number | undefined {
    switch (dataType) {
        case 'usage':
            return entry.usage
        case 'demandAverage':
            return entry.demandAverage
        case 'demandMax':
            return entry.demandMax
        default:
            return entry.usage
    }
}

function getEntryIdForHighlight(
    entry: IntervalDataEntry,
    display: IntervalDisplayParam
): number | undefined {
    return display === 'subMeters' ? entry.subMeterId : entry.meterId
}

export type TopAggregateEntry = {
    timestamp: number
    value: number
    tsStr: string
}
export type TopCompareEntry = {
    meterId: number
    timestamp: number
    timestampStr: string
    value: number
}

/** Build highlights from a pre-sorted top list (no sort; list is maintained as we stream). */
function highlightsFromTopAggregate(
    top: TopAggregateEntry[]
): IntervalHighlightsState {
    const highlightKeySet = new Set(top.map((p) => String(p.timestamp)))
    const highlightPoints = top.map((p) => ({
        timestamp: p.timestamp,
        value: p.value,
    }))
    return { highlightKeySet, highlightPoints }
}

/** Build highlights from a pre-sorted top list (no sort; list is maintained as we stream). */
function highlightsFromTopCompare(
    top: TopCompareEntry[]
): IntervalHighlightsState {
    const highlightKeySet = new Set(
        top.map((p) => `${p.timestampStr}|${String(p.meterId)}`)
    )
    const highlightPoints = top.map((p) => ({
        timestamp: p.timestamp,
        value: p.value,
        meterId: p.meterId,
    }))
    return { highlightKeySet, highlightPoints }
}

/**
 * Update running top-N aggregate list when a timestamp sum changes.
 * Keeps at most INTERVAL_HIGHLIGHTS_AGGREGATE_TOP entries, sorted by value desc.
 */
function updateTopAggregate(
    top: TopAggregateEntry[],
    tsStr: string,
    timestampNum: number,
    newSum: number
): void {
    const filtered = top.filter((e) => e.tsStr !== tsStr)
    filtered.push({ timestamp: timestampNum, value: newSum, tsStr })
    filtered.sort((a, b) => b.value - a.value)
    top.length = 0
    top.push(...filtered.slice(0, INTERVAL_HIGHLIGHTS_AGGREGATE_TOP))
}

/**
 * Update running top-N compare list when a meter's max changes.
 * Keeps at most INTERVAL_HIGHLIGHTS_COMPARE_CAP entries, sorted by value desc.
 */
function updateTopCompare(
    top: TopCompareEntry[],
    meterId: number,
    entry: { timestamp: number; timestampStr: string; value: number }
): void {
    const filtered = top.filter((e) => e.meterId !== meterId)
    filtered.push({ meterId, ...entry })
    filtered.sort((a, b) => b.value - a.value)
    top.length = 0
    top.push(...filtered.slice(0, INTERVAL_HIGHLIGHTS_COMPARE_CAP))
}

/**
 * Process a single chunk from the interval data stream and update highlights.
 * Maintains running aggregates for highlight computation without reprocessing the entire dataset.
 * @param prev - Previous accumulated response (data + highlights)
 * @param chunk - New data entry from stream
 * @param reportSettings - Report settings (dataGrouping, dataType, display)
 * @param runningAggregatesRef - Mutable ref containing running state for highlight computation
 * @param emptyHighlights - Empty highlights object to use when highlights are disabled
 * @returns Updated response with new chunk added and highlights recomputed
 */
export function processIntervalChunk(
    prev: StreamedIntervalDataResponse | undefined,
    chunk: IntervalDataEntry,
    reportSettings: IntervalReportSettings | undefined,
    runningAggregatesRef: React.MutableRefObject<{
        timestampSums: Map<string, number>
        perMeterMax: Map<
            number,
            { timestamp: number; timestampStr: string; value: number }
        >
        topAggregate: TopAggregateEntry[]
        topCompare: TopCompareEntry[]
    } | null>,
    emptyHighlights: IntervalHighlightsState
): StreamedIntervalDataResponse {
    const isNewStream = !prev || prev.data.length === 0
    const data: IntervalDataEntry[] = prev ? [...prev.data, chunk] : [chunk]

    if (!reportSettings) {
        return { data, highlights: emptyHighlights }
    }

    if (isNewStream) {
        runningAggregatesRef.current = {
            timestampSums: new Map(),
            perMeterMax: new Map(),
            topAggregate: [],
            topCompare: [],
        }
    }

    const ref = runningAggregatesRef.current
    if (!ref) {
        return { data, highlights: prev?.highlights ?? emptyHighlights }
    }
    const { timestampSums, perMeterMax, topAggregate, topCompare } = ref

    const value = getEntryValue(chunk, reportSettings.dataType)
    const tsStr = chunk.timestampLocal || chunk.date
    const timestampNum = tsStr ? dayjs.utc(tsStr).valueOf() : 0

    if (reportSettings.dataGrouping === 'aggregate') {
        if (value !== undefined && tsStr) {
            const prevSum = timestampSums.get(tsStr) ?? 0
            const newSum = prevSum + value
            timestampSums.set(tsStr, newSum)
            updateTopAggregate(topAggregate, tsStr, timestampNum, newSum)
        }
    } else {
        const meterId = getEntryIdForHighlight(chunk, reportSettings.display)
        if (value !== undefined && meterId !== undefined && tsStr) {
            const existing = perMeterMax.get(meterId)
            if (!existing || value > existing.value) {
                const entry = {
                    timestamp: timestampNum,
                    timestampStr: String(timestampNum),
                    value,
                }
                perMeterMax.set(meterId, entry)
                updateTopCompare(topCompare, meterId, entry)
            }
        }
    }

    // Highlights from running top lists (O(k) map, k ≤ 5 or 20)
    const highlights =
        reportSettings.dataGrouping === 'aggregate'
            ? highlightsFromTopAggregate(topAggregate)
            : highlightsFromTopCompare(topCompare)

    return { data, highlights }
}

export const useActiveManagementApi = () => {
    const accessToken = useAuthStore((state) => state.accessToken)
    const axiosInstance = useAxiosInstance(accessToken)

    const customerId =
        useAuthStore((state) => state.customer?.customerId.toString()) ?? ''

    const useIntervalDataQuery = (
        params: Partial<GetActiveManagementRequestParams>,
        options: useIntervalDataQueryOpts = { enabled: true }
    ) => {
        const apiParams = useMemo(
            () => getIntervalDataApiParams(params),
            [params]
        )
        const query = useQuery({
            queryKey: ['interval-data', customerId, apiParams, accessToken],
            queryFn: async () => {
                try {
                    const response =
                        await axiosInstance.get<GetIntervalDataResponseBody>(
                            `/api/v1/customers/${customerId}/interval-data`,
                            { params: apiParams }
                        )
                    options.onSuccess?.(response.data)
                    return response.data
                } catch (error) {
                    options.onError?.()
                    throw error
                }
            },

            enabled:
                options.enabled !== false &&
                !!accessToken &&
                !!customerId &&
                !!apiParams['nodeIds[]'] &&
                !!apiParams.startDate &&
                !!apiParams.endDate &&
                !!apiParams.timezone,
            placeholderData: (prev) => prev,
        })

        return query
    }

    const useIntervalDataStreamedQuery = (
        params: Partial<GetActiveManagementRequestParams>,
        options: useIntervalDataStreamingQueryOpts = { enabled: true }
    ) => {
        const baseURL = import.meta.env.VITE_API_BASE_URL as string
        const url = `${baseURL}/api/v1/customers/${customerId}/interval-data`

        // Build query string from params
        const buildQueryString = useCallback(
            (
                requestParams: Partial<GetActiveManagementRequestParams>
            ): string => {
                const searchParams = new URLSearchParams()
                for (const [key, value] of Object.entries(requestParams)) {
                    if (value) {
                        searchParams.append(key, String(value))
                    }
                }
                return searchParams.toString()
            },
            []
        )

        const apiParams = useMemo(
            () => getIntervalDataApiParams(params),
            [params]
        )
        const queryString = useMemo(
            () => buildQueryString(apiParams),
            [buildQueryString, apiParams]
        )
        const fullUrl = useMemo(
            () => (queryString ? `${url}?${queryString}` : url),
            [url, queryString]
        )

        // Create async generator for streaming NDJSON
        const streamFn = useCallback(
            async function* (
                signal: AbortSignal
            ): AsyncGenerator<IntervalDataEntry, void, unknown> {
                // Read accessToken directly from store to always get the latest value
                // This ensures we use the refreshed token when it changes
                const currentAccessToken = useAuthStore.getState().accessToken
                let response: Response
                try {
                    response = await fetch(fullUrl, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${currentAccessToken}`,
                            'X-Trace-Id': uuidv4(),
                            Accept: 'application/x-ndjson',
                        },
                        signal,
                    })
                } catch (error) {
                    // Handle AbortError gracefully
                    if (error instanceof Error && error.name === 'AbortError') {
                        return
                    }
                    options.onError?.()
                    throw error
                }

                if (!response.ok) {
                    options.onError?.()
                    throw new Error(
                        `Failed to fetch interval data: ${String(response.status)} ${response.statusText}`
                    )
                }

                if (!response.body) {
                    options.onError?.()
                    throw new Error('Response body is null')
                }

                const reader = response.body.getReader()
                const decoder = new TextDecoder()
                let buffer = ''

                try {
                    for (;;) {
                        let result: ReadableStreamReadResult<Uint8Array>
                        try {
                            result = await reader.read()
                        } catch (error) {
                            // Handle AbortError from reader.read()
                            if (
                                error instanceof Error &&
                                error.name === 'AbortError'
                            ) {
                                break
                            }
                            options.onError?.()
                            throw error
                        }

                        const { done, value } = result

                        if (done) {
                            // Process any remaining data in buffer
                            if (buffer.trim()) {
                                const lines = buffer
                                    .split('\n')
                                    .filter((line) => line.trim())
                                for (const line of lines) {
                                    try {
                                        const parsed = JSON.parse(
                                            line
                                        ) as IntervalDataEntry
                                        yield parsed
                                    } catch (error) {
                                        logError(error, {
                                            source: 'useIntervalDataStreamedQuery',
                                            rawLine: line,
                                            context:
                                                'NDJSON parse error (final buffer)',
                                        })
                                    }
                                }
                            }
                            break
                        }

                        // Decode chunk and add to buffer
                        buffer += decoder.decode(value, { stream: true })

                        // Process complete lines (ending with newline)
                        const lines = buffer.split('\n')
                        // Keep the last incomplete line in buffer
                        buffer = lines.pop() || ''

                        for (const line of lines) {
                            if (line.trim()) {
                                try {
                                    const parsed = JSON.parse(
                                        line
                                    ) as IntervalDataEntry
                                    yield parsed
                                } catch (error) {
                                    logError(error, {
                                        source: 'useIntervalDataStreamedQuery',
                                        rawLine: line,
                                        context:
                                            'NDJSON parse error (stream chunk)',
                                    })
                                }
                            }
                        }
                    }
                } catch (error) {
                    options.onError?.()
                    throw error
                } finally {
                    reader.releaseLock()
                }
            },
            [fullUrl, options]
        )

        const reportSettings = options.reportSettings
        const runningAggregatesRef = useRef<{
            timestampSums: Map<string, number>
            perMeterMax: Map<
                number,
                { timestamp: number; timestampStr: string; value: number }
            >
            topAggregate: TopAggregateEntry[]
            topCompare: TopCompareEntry[]
        } | null>(null)
        const emptyHighlights: IntervalHighlightsState = useMemo(
            () => ({
                highlightKeySet: new Set(),
                highlightPoints: [],
            }),
            []
        )

        const reducer = useCallback(
            (
                prev: StreamedIntervalDataResponse | undefined,
                chunk: IntervalDataEntry
            ): StreamedIntervalDataResponse => {
                return processIntervalChunk(
                    prev,
                    chunk,
                    reportSettings,
                    runningAggregatesRef,
                    emptyHighlights
                )
            },
            [reportSettings, emptyHighlights]
        )

        const initialData: StreamedIntervalDataResponse = {
            data: [],
            highlights: {
                highlightKeySet: new Set(),
                highlightPoints: [],
            },
        }

        const onStreamEnd = useCallback(
            (
                data: StreamedIntervalDataResponse
            ): StreamedIntervalDataResponse => {
                if (!reportSettings) return data
                const ref = runningAggregatesRef.current
                if (!ref) return data
                const highlights =
                    reportSettings.dataGrouping === 'aggregate'
                        ? highlightsFromTopAggregate(ref.topAggregate)
                        : highlightsFromTopCompare(ref.topCompare)
                return { ...data, highlights }
            },
            [reportSettings]
        )

        const query = useStreamedQuery<
            StreamedIntervalDataResponse,
            IntervalDataEntry
        >({
            queryKey: [
                'interval-data-streamed',
                customerId,
                apiParams,
                accessToken,
                reportSettings
                    ? {
                          dataGrouping: reportSettings.dataGrouping,
                          dataType: reportSettings.dataType,
                          display: reportSettings.display,
                      }
                    : null,
            ],
            streamFn,
            reducer,
            initialData,
            onStreamEnd,
            enabled:
                options.enabled !== false &&
                !!accessToken &&
                !!customerId &&
                !!apiParams['nodeIds[]'] &&
                !!apiParams.startDate &&
                !!apiParams.endDate &&
                !!apiParams.timezone,
            placeholderData: (prev) => prev,
        })

        // Call onSuccess when query completes successfully with data
        const onSuccessRef = useRef(options.onSuccess)
        onSuccessRef.current = options.onSuccess

        useEffect(() => {
            if (query.isSuccess && query.data && onSuccessRef.current) {
                onSuccessRef.current(query.data)
            }
        }, [query.isSuccess, query.data])

        const downloadXlsx = useCallback(async () => {
            const currentAccessToken = useAuthStore.getState().accessToken
            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${currentAccessToken}`,
                    'X-Trace-Id': uuidv4(),
                    Accept: 'application/vnd.ms-excel',
                },
            })

            if (!response.ok) {
                const body = (await response.json().catch(() => undefined)) as
                    | IntervalDataExportErrorBody
                    | undefined

                const details = body?.details
                const errorIdentifier = details?.errorIdentifier
                const count = Number(details?.count)
                const limit = Number(details?.limit)

                const error = new Error(
                    `Failed to download interval data: ${String(response.status)} ${response.statusText}`
                )

                throw Object.assign(error, {
                    body,
                    errorIdentifier,
                    count,
                    limit,
                })
            }

            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            const filename = `interval-data-${dayjs().format('MM-DD-YYYY')}.xlsx`

            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        }, [fullUrl])

        return { ...query, downloadXlsx }
    }

    const useTimezonesQuery = (params: Partial<GetTimezonesRequestParams>) => {
        return useQuery({
            queryKey: ['timezones', customerId, params, accessToken],
            queryFn: async () => {
                const response =
                    await axiosInstance.get<GetTimezonesResponseBody>(
                        `/api/v1/customers/${customerId}/timezones`,
                        {
                            params,
                        }
                    )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!params['nodeIds[]'],
            placeholderData: (prev) => prev,
        })
    }

    return {
        useIntervalDataQuery,
        useIntervalDataStreamedQuery,
        useTimezonesQuery,
    }
}

type useIntervalDataQueryOpts = {
    enabled?: boolean
    onSuccess?: (data: GetIntervalDataResponseBody) => void
    onError?: () => void
}

type useIntervalDataStreamingQueryOpts = {
    enabled?: boolean
    reportSettings?: IntervalReportSettings
    onSuccess?: (data: StreamedIntervalDataResponse) => void
    onError?: () => void
}
